/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.5.3 -k -b model-vw-id4.glb 
*/
import React, { useEffect, useRef, useCallback, forwardRef } from 'react'
import { useFrame, useGraph } from '@react-three/fiber'
import { SkeletonUtils } from 'three-stdlib'
import { MeshStandardMaterial } from "three";

import { useGLTF } from '@react-three/drei'
import * as THREE from 'three';



const originalMaterials = new Map();

const redMaterial = new MeshStandardMaterial({
  color: 0xFF0000,
  metalness: 0.5,
  roughness: 1,
  name: "red",
  transparent: true,
  opacity: 0.7,
});

const orangeMaterial = new MeshStandardMaterial({
  color: 0xfb8500,
  metalness: 0.5,
  roughness: 1,
  name: "orange"
});

const yellowMaterial = new MeshStandardMaterial({
  color: 0xff7b00,
  metalness: 0.5,
  roughness: 5,
  name: "yellow"
});

const VWID4 = forwardRef(({ vehicle, isTransparent = false, isCrashed = false, onLoad, ...props }, ref) => {
  const { scene } = useGLTF('/3d/model-vw-id4.glb');
  const clone = React.useMemo(() => SkeletonUtils.clone(scene), [scene])
  const { nodes, materials } = useGraph(clone)

  const ORIGINAL_OPACITY = 0.3
  const rotationSpeed = 0.02;
  const maxRotationZLeft = Math.PI / 3;
  const minRotationZLeft = 0;
  const minRotationZRight = -Math.PI / 3;
  const maxRotationZRight = 0;

  const minTrunkRotation = 0;
  const maxTrunkRotation = Math.PI / 3;
  const blinkInterval = 500;

  const DoorFrontLeftOpenRef = useRef();
  const DoorFrontRightOpenRef = useRef();
  const DoorRearLeftOpenRef = useRef();
  const DoorRearRightOpenRef = useRef();

  const TireFrontLeftRef = useRef();
  const TireFrontRightRef = useRef();
  const TireRearLeftRef = useRef();
  const TireRearRightRef = useRef();

  const BrakeWornFrontLeftRef = useRef();
  const BrakeWornFrontRightRef = useRef();
  const BrakeWornRearLeftRef = useRef();
  const BrakeWornRearRightRef = useRef();

  const HazardLightFrontLeftIsSignalingRef = useRef();
  const HazardLightFrontRightIsSignalingRef = useRef();
  const HazardLightRearLeftIsSignalingRef = useRef();
  const HazardLightRearRightIsSignalingRef = useRef();

  const TrunkOpenRef = useRef();

  const EngineRef = useRef();



  useEffect(() => {

    clone.current?.traverse((child) => {
      if (!child.isMesh) return;
      // Ensure each mesh has its own material instance before mutation
      if (child.material && !child.material.isCloned) {
        child.material = child.material.clone();
        child.material.isCloned = true;
      }
      const redCrashedMeshes = ["Front_RedPart"];
      if (isCrashed && redCrashedMeshes.includes(child.name)) {
        child.material = redMaterial;
      }
      if (isTransparent && child.material.name !== "orange") {
        child.material.transparent = true;
        child.material.opacity = ORIGINAL_OPACITY;
        // console.log("turning" + child.name +"transparent")
      }
      child.material.side = THREE.DoubleSide;
    });

    onLoad?.();
  }, [clone, isTransparent, isCrashed, onLoad]);


  const handleDoorsAnimation = useCallback(() => {
    if (DoorFrontLeftOpenRef.current) {
      const currentRotationZ = DoorFrontLeftOpenRef.current.rotation.z;
      const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row1?.DriverSide?.IsOpen;

      if (isDoorOpen && currentRotationZ < maxRotationZLeft) {
        DoorFrontLeftOpenRef.current.rotation.z += rotationSpeed; // Open the door
      } else if (!isDoorOpen && currentRotationZ > minRotationZLeft) {
        DoorFrontLeftOpenRef.current.rotation.z -= rotationSpeed; // Close the door
      }
    }


    if (DoorRearLeftOpenRef.current) {
      const currentRotationZ = DoorRearLeftOpenRef.current.rotation.z;
      const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row2?.DriverSide?.IsOpen;

      if (isDoorOpen && currentRotationZ < maxRotationZLeft) {
        DoorRearLeftOpenRef.current.rotation.z += rotationSpeed; // Open the door
      } else if (!isDoorOpen && currentRotationZ > minRotationZLeft) {
        DoorRearLeftOpenRef.current.rotation.z -= rotationSpeed; // Close the door
      }
    }

    if (DoorFrontRightOpenRef.current) {
      const currentRotationZ = DoorFrontRightOpenRef.current.rotation.z;
      const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row1?.PassengerSide?.IsOpen;

      if (isDoorOpen && currentRotationZ > minRotationZRight) {
        DoorFrontRightOpenRef.current.rotation.z -= rotationSpeed; // Open the door
      } else if (!isDoorOpen && currentRotationZ < maxRotationZRight) {
        DoorFrontRightOpenRef.current.rotation.z += rotationSpeed; // Close the door
      }
    }

    if (DoorRearRightOpenRef.current) {
      const currentRotationZ = DoorRearRightOpenRef.current.rotation.z;
      const isDoorOpen = vehicle?.Vehicle?.Cabin?.Door?.Row2?.PassengerSide?.IsOpen;

      if (isDoorOpen && currentRotationZ > minRotationZRight) {
        DoorRearRightOpenRef.current.rotation.z -= rotationSpeed; // Open the door
      } else if (!isDoorOpen && currentRotationZ < maxRotationZRight) {
        DoorRearRightOpenRef.current.rotation.z += rotationSpeed; // Close the door
      }
    }
  }, [
    minRotationZRight,
    maxRotationZLeft,
    vehicle?.Vehicle?.Cabin?.Door?.Row1?.DriverSide?.IsOpen,
    vehicle?.Vehicle?.Cabin?.Door?.Row2?.DriverSide?.IsOpen,
    vehicle?.Vehicle?.Cabin?.Door?.Row1?.PassengerSide?.IsOpen,
    vehicle?.Vehicle?.Cabin?.Door?.Row2?.PassengerSide?.IsOpen,
  ])

  const handleTrunkAnimation = useCallback(() => {
    if (TrunkOpenRef.current) {
      const currentTrunkRotation = TrunkOpenRef.current.rotation.x - (Math.PI / 2);
      const isTrunkOpen = vehicle?.Vehicle?.Body?.Trunk?.Rear?.IsOpen;
      const target = isTrunkOpen ? maxTrunkRotation : minTrunkRotation;

      if (Math.abs(currentTrunkRotation - target) > 0.01) {
        let nextRotation = currentTrunkRotation + Math.sign(target - currentTrunkRotation) * rotationSpeed;
        nextRotation = THREE.MathUtils.clamp(
          nextRotation,
          Math.min(minTrunkRotation, maxTrunkRotation),
          Math.max(minTrunkRotation, maxTrunkRotation)
        );

        TrunkOpenRef.current.rotation.x = nextRotation + Math.PI / 2;
      }
    }
  }, [minTrunkRotation, maxTrunkRotation, vehicle?.Vehicle?.Body?.Trunk?.Rear?.IsOpen]);

  const handleSpeedAnimation = useCallback((deltaTime) => {
    const speed = vehicle?.Vehicle?.Speed;
    const wheelRadius = 19;
    const angularSpeed = speed / wheelRadius;

    const wheels = [
      { ref: TireFrontLeftRef },
      { ref: TireFrontRightRef },
      { ref: TireRearLeftRef },
      { ref: TireRearRightRef },
    ];

    wheels.forEach(({ ref, }) => {
      const wheel = ref.current;
      if (wheel) {
        wheel.rotation.x += angularSpeed * deltaTime;
      }
    });
  }, [vehicle?.Vehicle?.Speed]);



  const handleTireLowPressure = useCallback(() => {
    const tires = [
      {
        ref: TireFrontLeftRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow
      },
      {
        ref: TireFrontRightRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow
      },
      {
        ref: TireRearLeftRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow
      },
      {
        ref: TireRearRightRef,
        pressureLow: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow
      }
    ];
    tires.forEach(({ ref, pressureLow }) => {
      if (!ref.current) return;

      const name = ref.current.name;
      const currentMaterial = ref.current.material;

      if (pressureLow) {
        if (!originalMaterials.has(name)) {
          originalMaterials.set(name, currentMaterial);
        }
        ref.current.material = redMaterial;
      } else if (originalMaterials.has(name)) {
        ref.current.material = originalMaterials.get(name);
      }
    });
  }, [
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow,
  ]);



  const handleBrakeWorn = useCallback(() => {
    const brakes = [
      {
        ref: BrakeWornFrontLeftRef,
        tire: TireFrontLeftRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn
      },
      {
        ref: BrakeWornFrontRightRef,
        tire: TireFrontRightRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn
      },
      {
        ref: BrakeWornRearLeftRef,
        tire: TireRearLeftRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn
      },
      {
        ref: BrakeWornRearRightRef,
        tire: TireRearRightRef,
        isWorn: vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn
      }
    ];
    brakes.forEach(({ ref, isWorn }) => {
      if (!ref.current) return;

      const name = ref.current.name;
      const currentMaterial = ref.current.material;

      if (isWorn) {
        if (!originalMaterials.has(name)) {
          originalMaterials.set(name, currentMaterial);
        }

        if (
          vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn &&
          vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn &&
          vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn &&
          vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn
        ) {
          ref.current.material = redMaterial;
        } else {
          ref.current.material = orangeMaterial;
        }

      } else if (originalMaterials.has(name)) {
        ref.current.material = originalMaterials.get(name);
      }
    });
  }, [
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn,
  ]);


  const handleEngine = useCallback(() => {
    const engineOilTemperature = vehicle?.Vehicle.Powertrain.CombustionEngine.EOT;
    // console.log(engineOilTemperature)

    if (EngineRef.current) {
      if (!originalMaterials.has(EngineRef.current.name)) {
        const firstChildMaterial = EngineRef.current.children[0]?.material;
        if (firstChildMaterial) {
          originalMaterials.set(EngineRef.current.name, firstChildMaterial);
        }
      }

      if (engineOilTemperature >= 120) {
        // console.log(EngineRef.current)
        EngineRef.current.children.forEach(child => {
          if (child.material) {
            child.material = redMaterial;
          }
        });

      } else {

        const originalMaterial = originalMaterials.get(EngineRef.current.name);
        EngineRef.current.children.forEach(child => {
          if (child.material) {
            child.material = originalMaterial;
          }
        });
      }
    }
  }, [vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT]);


  const handleHazardLights = useCallback(() => {
    const hazardLightsIsSignaling = vehicle?.Vehicle?.Body?.Lights?.Hazard?.IsSignaling;
    if (HazardLightFrontLeftIsSignalingRef.current) {
      const hazardLights = [
        HazardLightFrontLeftIsSignalingRef.current,
        HazardLightFrontRightIsSignalingRef.current,
        HazardLightRearLeftIsSignalingRef.current,
        HazardLightRearRightIsSignalingRef.current
      ];

      if (!originalMaterials.has(hazardLights[0].name)) {
        originalMaterials.set(hazardLights[0].name, hazardLights[0].material);
      }

      if (hazardLightsIsSignaling) {
        if (hazardLights[0].material === originalMaterials.get(hazardLights[0].name)) {
          hazardLights.forEach(light => {
            if (light) light.material = yellowMaterial;
          });
        }
        else {
          hazardLights.forEach(light => {
            if (light) light.material = originalMaterials.get(hazardLights[0].name);
          });
        }

      } else {
        hazardLights.forEach(light => {
          if (light) light.material = originalMaterials.get(hazardLights[0].name);
        });
      }
    }
  }, [vehicle?.Vehicle?.Body?.Lights?.Hazard?.IsSignaling]);

  useEffect(() => {
    const interval = setInterval(handleHazardLights, blinkInterval);

    return () => {
      clearInterval(interval);
    }
  }, [handleHazardLights]);


  useFrame(() => {
    handleDoorsAnimation();
    handleTrunkAnimation();
    handleSpeedAnimation(0.1);
    handleTireLowPressure();
    handleBrakeWorn();
    handleEngine();

  });



  return (
    <group ref={ref} {...props} position={[0, -0.7, -0.3]} dispose={null}>
      <group ref={clone}>
        <mesh name="Battery" geometry={nodes.Battery.geometry} material={nodes.Battery.material} position={[0, 0.328, 0.091]} scale={[0.63, 0.051, 0.898]} />
        <group ref={EngineRef} name="Engine">
          <mesh name="Electric_Engine" geometry={nodes.Electric_Engine.geometry} material={nodes.Electric_Engine.material} position={[0, 0.54, -1.229]} rotation={[Math.PI / 2, 0, -Math.PI / 2]} />
          <mesh name="Electric_Engine_2" geometry={nodes.Electric_Engine.geometry} material={nodes.Electric_Engine.material} position={[0, 0.54, 1.5]} rotation={[Math.PI / 2, 0, -Math.PI / 2]} />
        </group>
        <mesh name="Antenna" geometry={nodes.Antenna.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Center_Console" geometry={nodes.Center_Console.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Center_Console_Containers" geometry={nodes.Center_Console_Containers.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Dashboard" geometry={nodes.Dashboard.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Dashboard_Console_Base" geometry={nodes.Dashboard_Console_Base.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Dashboard_Top" geometry={nodes.Dashboard_Top.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Door_Front_Left" geometry={nodes.Door_Front_Left.geometry} material={materials['Material.001']} position={[0.879, 0.634, 0.928]} rotation={[Math.PI / 2, 0, 0]} ref={DoorFrontLeftOpenRef} />
        <mesh name="Door_Front_Right" geometry={nodes.Door_Front_Right.geometry} material={materials['Material.001']} position={[-0.879, 0.605, 0.927]} rotation={[Math.PI / 2, 0, 0]} ref={DoorFrontRightOpenRef} />
        <mesh name="Door_Rear_Left" geometry={nodes.Door_Rear_Left.geometry} material={materials['Material.001']} position={[0.852, 0.647, -0.169]} rotation={[Math.PI / 2, 0, 0]} ref={DoorRearLeftOpenRef} />
        <mesh name="Door_Rear_Right" geometry={nodes.Door_Rear_Right.geometry} material={materials['Material.001']} position={[-0.851, 0.661, -0.169]} rotation={[Math.PI / 2, 0, 0]} ref={DoorRearRightOpenRef} />
        <mesh name="Door_Seals" geometry={nodes.Door_Seals.geometry} material={materials['Material.001']} position={[0, 1.576, -1.43]} rotation={[Math.PI / 2, 0, 0]} ref={TrunkOpenRef} />
        <mesh name="Door_Seals001" geometry={nodes.Door_Seals001.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior" geometry={nodes.Exterior.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior002" geometry={nodes.Exterior002.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior004" geometry={nodes.Exterior004.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior006" geometry={nodes.Exterior006.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior008" geometry={nodes.Exterior008.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior009" geometry={nodes.Exterior009.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior_Black" geometry={nodes.Exterior_Black.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior_Black002" geometry={nodes.Exterior_Black002.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior_Chrome" geometry={nodes.Exterior_Chrome.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior_Front_Grill" geometry={nodes.Exterior_Front_Grill.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior_Metal" geometry={nodes.Exterior_Metal.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Exterior_Roof" geometry={nodes.Exterior_Roof.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        {/* <mesh name="Exterior_Roof_Glass_Cover" geometry={nodes.Exterior_Roof_Glass_Cover.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} /> */}
        <mesh name="Front_Lights_Always_On_Turn_Light_Left" geometry={nodes.Front_Lights_Always_On_Turn_Light_Left.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_Always_On_Turn_Light_Right" geometry={nodes.Front_Lights_Always_On_Turn_Light_Right.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_High_Beam_Left" geometry={nodes.Front_Lights_High_Beam_Left.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_High_Beam_Right" geometry={nodes.Front_Lights_High_Beam_Right.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_Inner_Glass" geometry={nodes.Front_Lights_Inner_Glass.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_Left_Signal" geometry={nodes.Front_Lights_Left_Signal.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} ref={HazardLightFrontLeftIsSignalingRef} />
        <mesh name="Front_Lights_Low_Beam_Left" geometry={nodes.Front_Lights_Low_Beam_Left.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_Low_Beam_Right" geometry={nodes.Front_Lights_Low_Beam_Right.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_Main_Light_Line" geometry={nodes.Front_Lights_Main_Light_Line.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Lights_Right_Signal" geometry={nodes.Front_Lights_Right_Signal.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} ref={HazardLightFrontRightIsSignalingRef} />
        <mesh name="Front_Lights_Signal_Clear" geometry={nodes.Front_Lights_Signal_Clear.geometry} material={nodes.Front_Lights_Signal_Clear.material} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_RedPart" geometry={nodes.Front_RedPart.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Front_Window" geometry={nodes.Front_Window.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Interior_Black" geometry={nodes.Interior_Black.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Interior_Chrome" geometry={nodes.Interior_Chrome.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Interior_Floor" geometry={nodes.Interior_Floor.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Interior_Glass" geometry={nodes.Interior_Glass.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Interior_Roof" geometry={nodes.Interior_Roof.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        {/* <mesh name="Interior_Roof_Glass_Cover" geometry={nodes.Interior_Roof_Glass_Cover.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} /> */}
        <mesh name="Interior_Sidearea" geometry={nodes.Interior_Sidearea.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Rear_Lights_Brake_Light_Left" geometry={nodes.Rear_Lights_Brake_Light_Left.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Rear_Lights_Brake_Light_Right" geometry={nodes.Rear_Lights_Brake_Light_Right.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Rear_Lights_Signal_Clear" geometry={nodes.Rear_Lights_Signal_Clear.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Rear_Lights_Signal_Clear_Left" geometry={nodes.Rear_Lights_Signal_Clear_Left.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} ref={HazardLightRearLeftIsSignalingRef} />
        <mesh name="Rear_Lights_Signal_Clear_Right" geometry={nodes.Rear_Lights_Signal_Clear_Right.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} ref={HazardLightRearRightIsSignalingRef} />
        <mesh name="Rear_Lights_Signal_Red" geometry={nodes.Rear_Lights_Signal_Red.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Rear_Lights_Turn_Light_Left" geometry={nodes.Rear_Lights_Turn_Light_Left.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Rear_Lights_Turn_Light_Right" geometry={nodes.Rear_Lights_Turn_Light_Right.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        {/* <mesh name="Rear_Trunk_Protector_Bottom" geometry={nodes.Rear_Trunk_Protector_Bottom.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} /> */}
        {/* <mesh name="Rear_Trunk_Protector_Bottom_Black" geometry={nodes.Rear_Trunk_Protector_Bottom_Black.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} /> */}
        {/* <mesh name="Rear_Trunk_Protector_Bottom_Handle" geometry={nodes.Rear_Trunk_Protector_Bottom_Handle.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} /> */}
        {/* <mesh name="Rear_Trunk_Protector_Top" geometry={nodes.Rear_Trunk_Protector_Top.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} /> */}
        <mesh name="Rear_View_Mirror" geometry={nodes.Rear_View_Mirror.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Rear_View_Mirror_Housing" geometry={nodes.Rear_View_Mirror_Housing.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        {/* <mesh name="Roof_Glass" geometry={nodes.Roof_Glass.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} /> */}
        <mesh name="Seats_Front_Black" geometry={nodes.Seats_Front_Black.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Front_Padding" geometry={nodes.Seats_Front_Padding.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Front_Siding" geometry={nodes.Seats_Front_Siding.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Front_Spaces" geometry={nodes.Seats_Front_Spaces.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Front_Structure" geometry={nodes.Seats_Front_Structure.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Rear_Backing" geometry={nodes.Seats_Rear_Backing.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Rear_Padding" geometry={nodes.Seats_Rear_Padding.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Rear_Structure" geometry={nodes.Seats_Rear_Structure.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Seats_Rear_Structure_Metal" geometry={nodes.Seats_Rear_Structure_Metal.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Side_Windows" geometry={nodes.Side_Windows.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Steering_Wheel_Column" geometry={nodes.Steering_Wheel_Column.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Steering_Wheel_Column_Ignition" geometry={nodes.Steering_Wheel_Column_Ignition.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Steering_Wheel_Column_Symbols" geometry={nodes.Steering_Wheel_Column_Symbols.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
        <mesh name="Steering_Wheel" geometry={nodes.Steering_Wheel.geometry} material={materials['Material.001']} position={[0.363, 1.04, 0.511]} />
        <mesh name="Steering_Wheel_Black" geometry={nodes.Steering_Wheel_Black.geometry} material={materials['Material.001']} position={[0.363, 1.186, 0.555]} />
        <mesh name="Steering_Wheel_Chrome" geometry={nodes.Steering_Wheel_Chrome.geometry} material={materials['Material.001']} position={[0.363, 1.011, 0.502]} />
        <mesh name="Steering_Wheel_Panels" geometry={nodes.Steering_Wheel_Panels.geometry} material={materials['Material.001']} position={[0.363, 0.97, 0.462]} />
        <mesh name="Steering_Wheel_Red" geometry={nodes.Steering_Wheel_Red.geometry} material={materials['Material.001']} position={[0.363, 0.98, 0.456]} />
        <mesh name="Steering_Wheel_Symbols" geometry={nodes.Steering_Wheel_Symbols.geometry} material={materials['Material.001']} position={[0.251, 1.063, 0.499]} />
        <mesh name="Tire_Front_Left_Disc" geometry={nodes.Tire_Front_Left_Disc.geometry} material={materials['Material.001']} position={[0.776, 0.386, 1.468]} ref={BrakeWornFrontLeftRef} />
        <mesh name="Tire_Front_Left_Disc001" geometry={nodes.Tire_Front_Left_Disc001.geometry} material={materials['Material.001']} position={[0.776, 0.386, 1.468]} />
        <mesh name="Tire_Front_Left_Rubber" geometry={nodes.Tire_Front_Left_Rubber.geometry} material={materials['Material.001']} position={[0.776, 0.386, 1.468]} ref={TireFrontLeftRef} />
        <mesh name="Tire_Front_Right_Disc" geometry={nodes.Tire_Front_Right_Disc.geometry} material={materials['Material.001']} position={[-0.776, 0.386, 1.468]} ref={BrakeWornFrontRightRef} />
        <mesh name="Tire_Front_Right_Disc001" geometry={nodes.Tire_Front_Right_Disc001.geometry} material={materials['Material.001']} position={[-0.776, 0.386, 1.468]} />
        <mesh name="Tire_Front_Right_Rubber" geometry={nodes.Tire_Front_Right_Rubber.geometry} material={materials['Material.001']} position={[-0.776, 0.386, 1.468]} ref={TireFrontRightRef} />
        <mesh name="Tire_Rear_Left_Caliper" geometry={nodes.Tire_Rear_Left_Caliper.geometry} material={materials['Material.001']} position={[0.776, 0.386, -1.305]} />
        <mesh name="Tire_Rear_Left_Caliper001" geometry={nodes.Tire_Rear_Left_Caliper001.geometry} material={materials['Material.001']} position={[0.776, 0.386, -1.305]} ref={BrakeWornRearLeftRef} />
        <mesh name="Tire_Rear_Left_Rubber" geometry={nodes.Tire_Rear_Left_Rubber.geometry} material={materials['Material.001']} position={[0.776, 0.386, -1.305]} ref={TireRearLeftRef} />
        <mesh name="Tire_Rear_Right_Caliper" geometry={nodes.Tire_Rear_Right_Caliper.geometry} material={materials['Material.001']} position={[-0.776, 0.386, -1.305]} />
        <mesh name="Tire_Rear_Right_Caliper001" geometry={nodes.Tire_Rear_Right_Caliper001.geometry} material={materials['Material.001']} position={[-0.776, 0.386, -1.305]} ref={BrakeWornRearRightRef} />
        <mesh name="Tire_Rear_Right_Rubber" geometry={nodes.Tire_Rear_Right_Rubber.geometry} material={materials['Material.001']} position={[-0.776, 0.386, -1.305]} ref={TireRearRightRef} />
        <mesh name="Windshield_Wipers" geometry={nodes.Windshield_Wipers.geometry} material={materials['Material.001']} rotation={[Math.PI / 2, 0, 0]} />
      </group>
    </group>
  )
});

export { VWID4 };

useGLTF.preload('/3d/model-vw-id4.glb')




export const vwID4OccupantPositions = {
  Row1: {
    DriverSide: [0.4, -0.25, 0.55],
    PassengerSide: [-0.4, -0.25, 0.55],
  },
  Row2: {
    DriverSide: [0.5, -0.25, -0.3],
    PassengerSide: [-0.5, -0.25, -0.3],
    Middle: [0, -0.25, -0.3],
  },
};


export const vwID4CameraPositions = {
  initialPosition: [0, 10, 0.01],
  frontPosition: [0, 1.3, 6.5]
}